import React, { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Pencil, Trash2, Check } from "lucide-react";
import { VendorPicker } from "@/components/VendorPicker";

const UNITS = ["kg", "gr", "lt", "ml", "τεμ", "δεσμ", "φακ"];
const inp = "w-full rounded-md border border-transparent bg-transparent px-1 py-0.5 text-xs outline-none hover:border-[#E3DED4] focus:border-[#45423C] focus:bg-white";

/* Text/number cell that saves on blur or Enter; Escape reverts. */
const Cell = ({ value, onSave, type = "text", testid, className = "", list, disabled, placeholder, step = "0.001" }) => {
  const [v, setV] = useState(value ?? "");
  useEffect(() => { setV(value ?? ""); }, [value]);
  const commit = () => { if (String(v) !== String(value ?? "")) onSave(type === "number" ? Number(v) || 0 : v); };
  return (
    <input data-testid={testid} type={type} step={type === "number" ? step : undefined} list={list} value={v} disabled={disabled} placeholder={placeholder}
      onChange={(e) => setV(e.target.value)} onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") { setV(value ?? ""); e.currentTarget.blur(); } }}
      className={`${inp} ${className} disabled:hover:border-transparent`} />
  );
};

const CatSelect = ({ value, options, onSave, testid, disabled, className = "" }) => (
  <select data-testid={testid} value={value || ""} disabled={disabled} onChange={(e) => onSave(e.target.value)} className={`${inp} ${className} disabled:hover:border-transparent`}>
    <option value="">—</option>
    {options.map((c) => <option key={c} value={c}>{c}</option>)}
    {value && !options.includes(value) && <option value={value}>{value} (εκτός λίστας)</option>}
  </select>
);

export const CatalogRow = ({ r, vendors, cats = [], units = UNITS, onEdit, onChanged, canEdit }) => {
  const opts = { onSuccess: onChanged, onError: (e) => toast.error(apiErr(e)) };
  const invite = useMutation({ mutationFn: async (ids) => (await api.put("/procurement/bidding/invite", { item_id: r.id, vendor_ids: ids })).data, ...opts });
  const pref = useMutation({ mutationFn: async (v) => (await api.post("/procurement/bidding/preferred", { item_id: r.id, vendor_id: v })).data, ...opts });
  const patch = useMutation({ mutationFn: async (body) => (await api.patch(`/recipes/ingredients/${r.id}`, body)).data, ...opts });
  const del = useMutation({
    mutationFn: async () => (await api.delete(`/recipes/ingredients/${r.id}`)).data,
    onSuccess: () => { toast.success("Το είδος διαγράφηκε"); onChanged(); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const wants = r.needs_offer !== false;
  const stock = r.count_in_stock !== false;
  const ro = !canEdit || patch.isPending;
  const taf = Math.round((r.taf_pct ?? 1) * 100);
  const Flag = ({ on, onClick, title, testid }) => (
    <button data-testid={testid} onClick={onClick} disabled={ro} title={title}
      className={`h-5 w-5 rounded border inline-flex items-center justify-center transition-colors disabled:opacity-60 ${on ? "bg-[#6E7A63] border-[#6E7A63] text-white" : "bg-white border-[#D8D0C2]"}`}>
      {on && <Check className="h-3.5 w-3.5" />}
    </button>
  );
  const Pct = ({ field, value, testid, cls }) => (
    <span className={`inline-flex items-center rounded-md border border-[#E3DED4] bg-white overflow-hidden ${cls}`}>
      <input data-testid={testid} type="number" min="0" max="100" step="5" defaultValue={value} key={value} disabled={ro}
        onBlur={(e) => { const n = Math.max(0, Math.min(100, Number(e.target.value) || 0)); if (n !== value) patch.mutate({ [field]: n / 100 }); }}
        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        className="w-9 px-1 py-0.5 text-[11px] text-right tabular outline-none bg-transparent" />
      <span className="pr-1 text-[9px] text-[#9A948A]">%</span>
    </span>
  );
  return (
    <tr className="border-t border-[#EFEBE3] hover:bg-[#FBFAF7]" data-testid={`catalog-row-${r.id}`}>
      <td className="px-2 py-1 font-medium text-[#45423C]">
        <Cell testid={`catalog-name-${r.id}`} value={r.name} disabled={ro} className="font-medium text-sm" onSave={(v) => v.trim() && patch.mutate({ name: v })} />
        <div className="flex flex-wrap gap-x-2 lg:hidden">
          <CatSelect testid={`catalog-cat-m-${r.id}`} value={r.category} options={cats} disabled={ro} className="text-[11px] text-[#6F6A60] w-28" onSave={(v) => patch.mutate({ category: v })} />
          <Cell testid={`catalog-notes-m-${r.id}`} value={r.notes} disabled={ro} placeholder="σημειώσεις…" className="text-[11px] text-[#9A948A] flex-1" onSave={(v) => patch.mutate({ notes: v })} />
        </div>
        <Cell testid={`catalog-supplier-${r.id}`} value={r.supplier} disabled={ro} placeholder="τρέχων προμηθευτής…" className="text-[11px] text-[#B9AE9A] font-normal hidden lg:block" onSave={(v) => patch.mutate({ supplier: v })} />
      </td>
      <td className="px-1 py-1 text-[#6F6A60] hidden lg:table-cell w-[9%]"><CatSelect testid={`catalog-cat-${r.id}`} value={r.category} options={cats} disabled={ro} onSave={(v) => patch.mutate({ category: v })} /></td>
      <td className="px-1 py-1 text-[#6F6A60] w-[52px]">
        <select data-testid={`catalog-unit-${r.id}`} value={r.unit || "kg"} disabled={ro} onChange={(e) => patch.mutate({ unit: e.target.value })} className={`${inp} w-auto pr-4`}>
          {units.map((u) => <option key={u}>{u}</option>)}
          {r.unit && !units.includes(r.unit) && <option value={r.unit}>{r.unit}</option>}
        </select>
      </td>
      <td className="px-1 py-1 text-right tabular w-[70px]"><Cell testid={`catalog-price-${r.id}`} type="number" value={r.price_per_unit ?? 0} disabled={ro} className="text-right" onSave={(v) => patch.mutate({ price_per_unit: v })} /></td>
      <td className="px-1 py-1 text-right tabular w-[70px] hidden md:table-cell"><Cell testid={`catalog-target-${r.id}`} type="number" value={r.price_target ?? 0} disabled={ro} className="text-right text-[#6F6A60]" onSave={(v) => patch.mutate({ price_target: v })} /></td>
      <td className="px-1 py-1 text-center whitespace-nowrap" data-testid={`catalog-alloc-${r.id}`}>
        <Pct field="taf_pct" value={taf} testid={`catalog-taf-${r.id}`} cls="" />
        <Pct field="tribe_pct" value={100 - taf} testid={`catalog-tribe-${r.id}`} cls="ml-1" />
      </td>
      <td className="px-1 py-1 text-center"><Flag testid={`catalog-needs-${r.id}`} on={wants} onClick={() => patch.mutate({ needs_offer: !wants })} title={wants ? "Θέλω προσφορά" : "Χωρίς προσφορά"} /></td>
      <td className="px-1 py-1 text-center hidden md:table-cell"><Flag testid={`catalog-stock-${r.id}`} on={stock} onClick={() => patch.mutate({ count_in_stock: !stock })} title={stock ? "Μετράει στην απογραφή" : "Εκτός απογραφής"} /></td>
      <td className="px-1 py-1">
        <VendorPicker testid={`catalog-offer-${r.id}`} vendors={vendors} selected={r.offer_vendor_ids} disabled={invite.isPending || !canEdit} dim={!wants} onChange={(ids) => invite.mutate(ids)} />
      </td>
      <td className="px-1 py-1 hidden xl:table-cell">
        <select data-testid={`catalog-pref-${r.id}`} value={r.preferred_vendor_id || ""} disabled={!canEdit} onChange={(e) => pref.mutate(e.target.value)}
          className="rounded-md border border-[#E3DED4] px-1.5 py-1 text-[11px] max-w-[130px] bg-white">
          <option value="">—</option>
          {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
      </td>
      <td className="px-1 py-1 hidden lg:table-cell w-[10%]"><Cell testid={`catalog-notes-${r.id}`} value={r.notes} disabled={ro} placeholder="σημειώσεις…" className="text-[11px] text-[#6F6A60]" onSave={(v) => patch.mutate({ notes: v })} /></td>
      <td className="px-1 py-1 whitespace-nowrap text-right w-[64px]">
        {canEdit && <>
        <button data-testid={`catalog-edit-${r.id}`} onClick={onEdit} className="p-1 rounded-md text-[#6F6A60] hover:bg-[#F1EDE6]" title="Επεξεργασία"><Pencil className="h-3.5 w-3.5" /></button>
        <button data-testid={`catalog-del-${r.id}`} onClick={() => window.confirm(`Διαγραφή «${r.name}»;`) && del.mutate()} className="p-1 rounded-md text-[#991B1B] hover:bg-[#FEF2F2]" title="Διαγραφή"><Trash2 className="h-3.5 w-3.5" /></button>
        </>}
      </td>
    </tr>
  );
};
